import { api } from '@/shared/api/http'

/** Mirrors the backend NotificationEvent enum. */
export type NotificationEventType =
  | 'PROFORMA_SUBMITTED'
  | 'APPROVAL_PENDING'
  | 'APPROVAL_REMINDER'
  | 'PROFORMA_LEVEL_APPROVED'
  | 'PROFORMA_APPROVED'
  | 'PROFORMA_REJECTED'
  | 'PROFORMA_RETURNED'
  | 'ERP_POST_FAILED'

export interface NotificationEntry {
  id: number
  eventType: NotificationEventType
  title: string
  summary: string | null
  docType: string | null
  docId: number | null
  docNo: string | null
  /** Relative path inside the portal, or null when there is nothing to open. */
  linkPath: string | null
  createdAt: string
  read: boolean
}

export interface NotificationPage {
  content: NotificationEntry[]
  page: number
  size: number
  totalElements: number
  totalPages: number
  first: boolean
  last: boolean
}

export interface UnreadCount {
  unread: number
}

export interface NotificationQuery {
  unreadOnly?: boolean
  page?: number
  size?: number
}

export const notificationKeys = {
  all: ['notifications'] as const,
  lists: ['notifications', 'list'] as const,
  list: (query: NotificationQuery) => ['notifications', 'list', query] as const,
  unread: ['notifications', 'unread'] as const,
}

export function fetchNotifications(query: NotificationQuery = {}) {
  const search = new URLSearchParams()
  search.set('unreadOnly', String(query.unreadOnly ?? false))
  search.set('page', String(query.page ?? 0))
  search.set('size', String(query.size ?? 20))
  return api.get<NotificationPage>(`/notifications?${search.toString()}`)
}

export function fetchUnreadCount() {
  return api.get<UnreadCount>('/notifications/unread-count')
}

// Both mark calls return the new unread count, so the badge is correct without
// a second round trip -- and correct even if another tab read something in the
// meantime, because the number comes from the server rather than from local
// arithmetic.
export function markNotificationRead(id: number) {
  return api.post<UnreadCount>(`/notifications/${id}/read`, {})
}

export function markAllNotificationsRead() {
  return api.post<UnreadCount>('/notifications/read-all', {})
}

/** The dot beside a row, by what the event means rather than by severity. */
export const EVENT_TONE: Record<NotificationEventType, string> = {
  PROFORMA_SUBMITTED: 'bg-gray-400',
  APPROVAL_PENDING: 'bg-primary',
  APPROVAL_REMINDER: 'bg-warning',
  // Progress, not an outcome: green would read as "finished" on a document
  // that still has levels to clear.
  PROFORMA_LEVEL_APPROVED: 'bg-gray-400',
  PROFORMA_APPROVED: 'bg-success',
  PROFORMA_REJECTED: 'bg-danger',
  PROFORMA_RETURNED: 'bg-warning',
  ERP_POST_FAILED: 'bg-danger',
}
